import { Box, FormControlLabel, Grid, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useAssignUserManagement from "../../../api/hooks/useAssignUserManagement";
import useColors from "../../../api/hooks/useColors";
import { usePOC } from "../../../api/hooks/usePOC";
import useTicketingIntegrations from "../../../api/hooks/useTicketingIntegrations";
import {
  controlKeys,
  platformUsersKeys,
  ticketKeys,
} from "../../../api/queryKeys";
import AssigneeLabel from "../../../components/assignee/AssigneeLabel";
import ExportButton from "../../../components/buttons/ExportButton";
import CustomSwitch from "../../../components/CustomSwitch";
import LoadingDialog from "../../../components/dialogs/LoadingDialog";
import CircleOption from "../../../components/filterOption/CircleOption";
import ImgOption from "../../../components/filterOption/SeverityOption";
import ClearFiltersButton, {
  resetFiltersToDefaults,
} from "../../../components/filters/ClearFiltersButton";
import FilterMenu from "../../../components/filters/FilterMenu";
import IconFilter from "../../../components/filters/IconFilter";
import EditFilterModal from "../../../components/filters/saved/EditFilterModal";
import SavedFiltersRow from "../../../components/filters/saved/SavedFiltersRow";
import SaveFiltersButton from "../../../components/filters/saved/SaveFiltersButton";
import SaveFiltersModal from "../../../components/filters/saved/SaveFiltersModal";
import SearchFilter from "../../../components/filters/SearchFilter";
import SearchInput from "../../../components/filters/SearchInput";
import SwitchFilter from "../../../components/filters/SwitchFilter";
import InitialScanBanner from "../../../components/InitialScanBanner";
import LimitsConfirmationDialog from "../../../components/poc/LimitsConfirmationDialog";
import AppPage from "../../../components/structure/AppPage";
import { CustomerFeatureFlagsContext } from "../../../contexts/CustomerFeatureFlagsContext";
import { EnvFeatureFlagContext } from "../../../contexts/EnvFeaturesContext";
import { POCContext } from "../../../contexts/POCContext";
import { useFileNaming } from "../../../hooks/useFileNaming";
import useSavedFilters from "../../../hooks/useSavedFilters";
import SvgToIcon from "../../../icons/SvgToIcon";
import {
  ComplexEntityTypes,
  ControlStatus,
  DRAWER_TABS,
  PAGES,
} from "../../../utils/constants";
import { searchObjectProperties } from "../../../utils/search";
import { EntityTypeInfo } from "../../stack/environment/components/constants";
import { ALL_OPTION_NAME } from "../../stack/environment/filters/utils";
import { icons } from "../../stack/environment/icons/iconExport";
import IntegrationRowFilter from "../steps/components/IntegrationRowFilter";
import ControlRemediationDrawer from "./components/ControlRemediationDrawer/ControlRemediationDrawer";
import ControlsTable from "./components/ControlsTable";
import NISTLabel from "./components/NISTLabel";
import { ReactComponent as NISTLogo } from "./components/NISTLogo.svg";
import { isRecentlyFound } from "./components/RecentlyFoundLabel";
import ScanMenuButton from "./components/ScanMenuButton";
import { SnoozeType } from "./components/SnoozeButton/constants";
import SnoozeButton from "./components/SnoozeButton/SnoozeButton";

import TimeFilter from "../../../components/filters/TimeFilter";
import { CONTROLS_CATEGORY, LogicNameFilter } from "../../../utils/constants";
import { getTimestampXDaysAgo } from "../../../utils/timestamp";
import { pastTimeOptions } from "../../stack/environment/filters/EntityTypeRegularFilters";
import { pastTimeValues } from "../../stack/environment/filters/utils";
import { IntegrationsMapping } from "../../stack/integrations/IntegrationsMapping";
import ControlCategoryFilter from "./components/ControlCategoryFilter";

const TICKET_FILTER_OPTIONS = {
  ALL: "All",
  WITH_TICKETS: "With tickets",
  WITHOUT_TICKETS: "Without tickets",
};

export default function ControlsView() {
  const colors = useColors();
  const { generateFileName } = useFileNaming();
  const [currentControls, setCurrentControls] = useState([]);
  const [selectedControl, setSelectedControl] = useState(null);
  const location = useLocation();
  const gridRef = useGridApiRef();
  const { isPOC } = useContext(POCContext);
  const { platformUsers } = useAssignUserManagement();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const { nistControlDemo } = useContext(EnvFeatureFlagContext);
  const prioritiesMap = { 1: "Low", 2: "Medium", 3: "High" };
  const [appliedFilters, setAppliedFilters] = useState({
    tools: {},
    nist_functions: {},
    controlStatuses: { Gap: true },
    priorities: {},
    showActiveDetection: false,
    showOnlyNewControls: false,
    searchString: "",
    entityTypes: {},
    myFavorites: false,
    assignee: {},
    ticketStatus: {},
    controlCategory: CONTROLS_CATEGORY.map((category) => [category, false]),
    firstSeen: {},
  });
  const [filterLogic, setFilterLogic] = useState({
    firstSeen: LogicNameFilter.TIME_AFTER,
  });
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const isLoading = false;
  const [isScanLoading, setIsScanLoading] = useState(false);
  const [selectedRowIDsToScan, setSelectedRowIDsToScan] = useState([]);
  //like selectedRowIDsToScan but unlike it can contain 2 aws controls
  const [selectedRowIDsToSnooze, setSelectedRowIDsToSnooze] = useState([]);
  const [filterOption, setFilterOptions] = useState({
    priorities: ["High", "Medium", "Low"],
    tools: [],
    controlStatuses: Object.values(ControlStatus),
    nist_functions: [],
    myFavorites: [],
    entityTypes: ["All", ...Object.values(ComplexEntityTypes)],
    assignee: [],
    ticketStatus: Object.values(TICKET_FILTER_OPTIONS),
    firstSeen: [],
  });
  const [, setSearchParams] = useSearchParams();
  const [controlIdClicked, setControlIdClicked] = useState(null);
  const { trackEvent, fetchAllControls, getFavoriteControls, getAllTickets } =
    useAPI();
  const filtersFromLink = location.state?.filters;
  const [openDrawer, setOpenDrawer] = useState(false);
  const queryClient = useQueryClient();

  // Use saved filters hook
  const {
    savedFilters,
    activeFilterId,
    showSaveModal,
    showEditModal,
    editingFilter,
    setShowSaveModal,
    setShowEditModal,
    handleSaveFilter,
    handleApplyFilter,
    handleEditFilter,
    handleUpdateFilter,
    handleDeleteFilter,
    handleUnselectFilter,
    isSaving,
    isUpdating,
  } = useSavedFilters({
    page: PAGES.CONTROLS,
    appliedFilters,
    setAppliedFilters,
    trackEvent,
    hasUserInteracted,
    setHasUserInteracted,
  });

  const resetSelection = useCallback(() => {
    gridRef.current?.setRowSelectionModel([]);
    setSelectedControl(null);
    setOpenDrawer(false);
    setSearchParams({});
  }, []);

  // Create resetAllFilter function using the exported utility
  const resetAllFilter = () => {
    resetFiltersToDefaults(appliedFilters, setAppliedFilters);
  };

  const { isJiraIntegrated, isAsanaIntegrated, isServiceNowIntegrated } =
    useTicketingIntegrations();

  const handleDrawerDisplay = useCallback((shouldDisplay) => {
    setOpenDrawer(shouldDisplay);
  }, []);

  const {
    viewedControls,
    pocDialogOpen,
    remainingControlsCount,
    dialogMessageFirstLine,
    dialogMessageSecondLine,
    dialogTitle,
    pocDataLoading,
    controlLoading,
    handleDialogClose,
    handlePOCClick,
    handlePOCDrawer,
  } = usePOC(handleDrawerDisplay, resetSelection);

  const { data: controls, isLoading: loadingControls } = useQuery({
    queryKey: controlKeys.all,
    queryFn: fetchAllControls,
    select: (data) =>
      data.data.map((control) => ({
        ...control,
        id: control.control_id,
      })),
  });

  const { data: fetchedFavoriteControls } = useQuery({
    queryKey: platformUsersKeys.favoriteControls(),
    queryFn: getFavoriteControls,
    select: (data) => data.data,
  });

  // Fetch all tickets for the customer
  const { data: allTickets, isLoading: isLoadingTickets } = useQuery({
    queryKey: ticketKeys.all,
    queryFn: async () => {
      const response = await getAllTickets();
      return response.data;
    },
    enabled: isJiraIntegrated || isAsanaIntegrated || isServiceNowIntegrated,
  });

  // Create a mapping of control_id to ticket for controls that are not per misconfiguration
  const ticketsByControlId = useMemo(() => {
    if (!allTickets) return new Map();

    const ticketMap = new Map();
    allTickets.forEach((ticket) => {
      if (ticket.control_id && !ticket.entity_id) {
        ticketMap.set(ticket.control_id, ticket);
      }
    });
    return ticketMap;
  }, [allTickets]);

  const doesControlAffectFilteredEntityTypes = (control) => {
    const selectedEntityTypes = Object.keys(appliedFilters.entityTypes).filter(
      (key) => appliedFilters.entityTypes[key] === true
    );

    if (selectedEntityTypes.length === 0) {
      return true;
    }

    return selectedEntityTypes.some(
      (entityType) =>
        control.affected_entity_counts_by_type[entityType.toLowerCase()] > 0
    );
  };

  useEffect(() => {
    if (controls?.length > 0) {
      if (selectedControl) {
        const control = controls.find(
          (control) => control.control_id === selectedControl.control_id
        );
        setSelectedControl(control);
        setOpenDrawer(true);
      }
      const toolsList = new Set();
      const nistFunctions = new Set();
      controls?.forEach((row) => {
        toolsList.add(row.tool);
        nistFunctions.add(row.nist_function);
      });
      const assignee = platformUsers;
      setFilterOptions({
        ...filterOption,
        tools: [...Array.from(toolsList).sort()],
        nist_functions: [...Array.from(nistFunctions).sort()],
        assignee: ["All", "None", ...assignee],
      });
    }
  }, [controls, platformUsers]);

  const onRowClick = async (row_obj) => {
    let control_id = row_obj.row.control_id;
    setControlIdClicked(control_id);
    trackEvent(
      `Click on control row with control id: ${row_obj.row.control_id}`
    );
    if (isPOC) {
      handlePOCClick(control_id);
    } else {
      setSearchParams({ control_id: control_id });
    }
  };

  const onRowSelection = (newSelection) => {
    setSelectedRowIDsToScan(newSelection);
    setSelectedRowIDsToSnooze(newSelection);
  };

  const handleTableExport = () => {
    const message =
      "User click on 'Export' button to download the controls table";
    trackEvent(message);
    const csvExportOptions = {
      fileName: generateFileName(`controls`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  useEffect(() => {
    const localSearchParams = new URLSearchParams(location.search);
    const pocCondition = (isPOC && !pocDataLoading) || !isPOC;
    if (
      localSearchParams.has("control_id") &&
      !loadingControls &&
      pocCondition
    ) {
      const controlId = localSearchParams.get("control_id");
      setControlIdClicked(controlId);
      if (isPOC) {
        handlePOCDrawer(controlId);
      } else {
        setOpenDrawer(true);
      }

      handleDrawer(controlId);
    }
  }, [location.search, loadingControls, pocDataLoading]);

  const handleDrawer = (control_id) => {
    const control = controls.find(
      (control) => control.control_id.toString() === control_id
    );
    setSelectedControl(control);
  };

  const doesControlAffectFilteredAssignee = (control) => {
    const assignee = Object.keys(appliedFilters.assignee).filter(
      (key) => appliedFilters.assignee[key] === true
    );
    if (assignee.length === 0) {
      return true;
    }
    if (assignee.includes("None") && !control.assignee_email) {
      return true;
    } else return assignee.includes(control.assignee_email);
  };

  const doesControlHaveTicket = (control) => {
    const selectedTicketStatuses = Object.keys(
      appliedFilters.ticketStatus
    ).filter((key) => appliedFilters.ticketStatus[key] === true);

    const hasTicket = ticketsByControlId.has(control.control_id);
    if (
      selectedTicketStatuses.includes(TICKET_FILTER_OPTIONS.WITH_TICKETS) &&
      selectedTicketStatuses.includes(TICKET_FILTER_OPTIONS.WITHOUT_TICKETS)
    ) {
      return true;
    } else if (
      selectedTicketStatuses.includes(TICKET_FILTER_OPTIONS.WITH_TICKETS)
    ) {
      return hasTicket;
    } else if (
      selectedTicketStatuses.includes(TICKET_FILTER_OPTIONS.WITHOUT_TICKETS)
    ) {
      return !hasTicket;
    }
    return true;
  };

  const doesControlHaveCategory = (control) => {
    const selectedCategories = Object.keys(
      appliedFilters.controlCategory
    ).filter((key) => appliedFilters.controlCategory[key] === true);
    if (selectedCategories.length === 0) {
      return true;
    }
    return selectedCategories.some((category) =>
      control.categories?.includes(category)
    );
  };

  const doesControlHaveFirstSeen = (control) => {
    const selectedFirstSeen = Object.keys(appliedFilters.firstSeen).filter(
      (key) => appliedFilters.firstSeen[key] === true
    );
    if (selectedFirstSeen.length === 0) {
      return true;
    }
    const firstSeenTimestamp = control.first_seen?.timestamp_utc;
    if (!firstSeenTimestamp) return false;
    let selectedTimestamp = 0;
    for (const [name, info] of Object.entries(pastTimeValues)) {
      if (appliedFilters.firstSeen[name]) {
        selectedTimestamp = getTimestampXDaysAgo(info.days);
        break;
      }
    }
    if (filterLogic.firstSeen === LogicNameFilter.TIME_AFTER) {
      return firstSeenTimestamp > selectedTimestamp; // the control was created after the selected timestamp
    } else if (filterLogic.firstSeen === LogicNameFilter.TIME_BEFORE) {
      return firstSeenTimestamp < selectedTimestamp; // the control was created before the selected timestamp
    } else {
      return false;
    }
  };

  useEffect(() => {
    trackEvent(`user change filters to ${JSON.stringify(appliedFilters)}`);

    if (currentControls && controls) {
      let filteredRows = controls;

      if (Object.keys(appliedFilters.tools)?.length > 0) {
        const tools_filter = Object.keys(appliedFilters.tools).filter(
          (key) => appliedFilters.tools[key] === true
        );
        if (tools_filter.length > 0) {
          filteredRows = filteredRows.filter((control) =>
            tools_filter.includes(control.tool)
          );
        }
      }

      if (Object.keys(appliedFilters.nist_functions)?.length > 0) {
        const filterFunctions = Object.keys(
          appliedFilters.nist_functions
        ).filter((key) => appliedFilters.nist_functions[key] === true);
        if (filterFunctions.length > 0) {
          filteredRows = filteredRows.filter((control) =>
            filterFunctions.includes(control.nist_function)
          );
        }
      }

      if (Object.keys(appliedFilters?.controlStatuses)?.length > 0) {
        const filterStatuses = Object.keys(
          appliedFilters.controlStatuses
        ).filter((key) => appliedFilters.controlStatuses[key] === true);
        if (filterStatuses.length > 0) {
          filteredRows = filteredRows.filter((control) => {
            return filterStatuses.includes(control.status);
          });
        }
      }

      if (Object.keys(appliedFilters.priorities)?.length > 0) {
        const filterPriorities = Object.keys(appliedFilters.priorities).filter(
          (key) => appliedFilters.priorities[key] === true
        );
        if (filterPriorities.length > 0) {
          filteredRows = filteredRows.filter((row) =>
            filterPriorities.includes(prioritiesMap[row.severity])
          );
        }
      }

      if (appliedFilters.showActiveDetection) {
        filteredRows = filteredRows.filter((control) => control.is_detected);
      }

      if (appliedFilters.showOnlyNewControls) {
        filteredRows = filteredRows.filter((control) =>
          isRecentlyFound(control.first_seen?.timestamp_utc)
        );
      }

      // Filter by entity types
      if (Object.keys(appliedFilters.entityTypes)?.length > 0) {
        filteredRows = filteredRows.filter((control) =>
          doesControlAffectFilteredEntityTypes(control)
        );
      }

      if (appliedFilters.myFavorites) {
        filteredRows = filteredRows.filter((control) =>
          fetchedFavoriteControls?.includes(control.control_id.toString())
        );
      }

      if (Object.keys(appliedFilters.assignee)?.length > 0) {
        filteredRows = filteredRows.filter((control) =>
          doesControlAffectFilteredAssignee(control)
        );
      }

      if (Object.keys(appliedFilters.ticketStatus)?.length > 0) {
        filteredRows = filteredRows.filter((control) =>
          doesControlHaveTicket(control)
        );
      }
      if (Object.keys(appliedFilters.controlCategory)?.length > 0) {
        filteredRows = filteredRows.filter((control) =>
          doesControlHaveCategory(control)
        );
      }
      if (Object.keys(appliedFilters.firstSeen)?.length > 0) {
        filteredRows = filteredRows.filter((control) =>
          doesControlHaveFirstSeen(control)
        );
      }

      if (appliedFilters.searchString !== "") {
        const searchString = appliedFilters.searchString;
        const parsedNumber = Number(searchString);
        const isNumber = !isNaN(parsedNumber);
        filteredRows = filteredRows.filter((row) => {
          if (isNumber) {
            return row.id && row.id.toString().includes(searchString);
          } else {
            return searchObjectProperties(row, searchString.toLowerCase());
          }
        });
      }
      // TODO: add a filter for active detections after https://linear.app/breeze-security/issue/DEV-2469/create-script-to-generate-mock-data-for-detections
      setCurrentControls(filteredRows);
    }
  }, [
    appliedFilters,
    controls,
    viewedControls,
    isLoading,
    ticketsByControlId,
    filterLogic,
  ]);

  useEffect(() => {
    // TODO: this is temporary, we will address this in future PR:
    // https://linear.app/breeze-security/issue/DEV-1845/store-the-selected-filter-in-backend
    if (filtersFromLink) {
      setAppliedFilters({
        ...appliedFilters,
        controlStatuses: {
          Gap: true,
          Compliant: true,
          Excluded: true,
          Snoozed: true,
        },
        ...filtersFromLink,
      });
    }
  }, [filtersFromLink]);

  const handleControlCategoryChange = (category, value) => {
    setAppliedFilters((prevFilters) => ({
      ...prevFilters,
      controlCategory: {
        ...CONTROLS_CATEGORY.map((category) => [category, false]),
        [category]: !prevFilters.controlCategory[category],
      },
    }));
  };

  return (
    <AppPage title="Controls & Gaps">
      <InitialScanBanner />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Grid container gap={"8px 8px"} wrap="wrap" alignItems="center">
          {/* ENTITY TYPE FILTERS */}
          <Box
            width="100%"
            padding="16px"
            display="flex"
            flexDirection="column"
            gap={"16px 8px"}
            sx={{
              borderRadius: "12px",
              backgroundColor: colors.grey_2percent,
              boxShadow: colors.entity_type_overview_top_shadow,
              marginBottom: "8px",
            }}
          >
            <Grid container gap={"12px"} wrap="wrap" alignItems="center">
              {CONTROLS_CATEGORY.map((category) => (
                <ControlCategoryFilter
                  selected={appliedFilters.controlCategory[category]}
                  label={category}
                  handleChange={handleControlCategoryChange}
                />
              ))}
            </Grid>
            {/* Saved Filters Row */}
            {savedFilters && savedFilters.length > 0 && (
              <Box marginBottom={1}>
                <SavedFiltersRow
                  savedFilters={savedFilters}
                  onApplyFilter={handleApplyFilter}
                  onEditFilter={handleEditFilter}
                  onDeleteFilter={handleDeleteFilter}
                  onUnselectFilter={() => handleUnselectFilter(resetAllFilter)}
                  activeFilterId={activeFilterId}
                />
              </Box>
            )}
            <Grid container gap={"8px 8px"} wrap="wrap" alignItems="center">
              {/* TOOL FILTER */}
              {/* this component is used to fix the issue that some icons are not displayed in the search filter */}
              <Box sx={{ display: "none" }}>
                {filterOption.tools.map((tool) => {
                  return <IntegrationRowFilter name={tool} />;
                })}
              </Box>
              <SearchFilter
                filterName={"Tool"}
                fieldName={"tools"}
                options={[
                  {
                    name: ALL_OPTION_NAME,
                    label: "All Tools",
                    displayName: "All",
                  },
                  ...filterOption.tools.map((tool) => ({
                    name: tool,
                    label: <IntegrationRowFilter name={tool} />,
                    displayName: IntegrationsMapping[tool]?.displayName,
                  })),
                ]}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                fullOptions={filterOption.tools}
              />
              {/* NIST FILTER */}
              {nistControlDemo && (
                <IconFilter
                  filterName={"Function"}
                  fieldName={"nist_functions"}
                  options={filterOption.nist_functions.map((nistFunction) => {
                    return {
                      name: nistFunction,
                      label: (
                        <Box sx={{ display: "flex" }} gap={1}>
                          <NISTLabel nistFunction={nistFunction} />
                          <Typography variant="caption">
                            {nistFunction}
                          </Typography>
                        </Box>
                      ),
                      displayName: nistFunction,
                    };
                  })}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                  icon={
                    <SvgToIcon
                      IconLogo={NISTLogo}
                      style={{ width: "35px", paddingLeft: "2px" }}
                    />
                  }
                  fullOptions={filterOption.nist_functions}
                />
              )}
              {/* SEVERITY FILTER */}
              <FilterMenu
                filterName={"Severity"}
                fieldName={"priorities"}
                options={filterOption.priorities.map((priority) => {
                  return {
                    name: priority,
                    label: <ImgOption type={"severity"} label={priority} />,
                    displayName: priority,
                  };
                })}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                fullOptions={filterOption.priorities}
              />
              {/* STATUS FILTER */}
              <FilterMenu
                filterName={"Status"}
                fieldName={"controlStatuses"}
                options={filterOption.controlStatuses.map((status) => {
                  return {
                    name: status,
                    label: <CircleOption label={status} type={"status"} />,
                    displayName: status,
                  };
                })}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                fullOptions={filterOption.controlStatuses}
                displayAs={"chip"}
              />
              {/* ENTITY TYPE FILTER */}
              <FilterMenu
                filterName="Affecting"
                fieldName="entityTypes"
                options={filterOption.entityTypes.map((entityType) => {
                  if (entityType === "All") {
                    return {
                      name: ALL_OPTION_NAME,
                      label: "All entity types",
                      displayName: "All",
                    };
                  }
                  const Icon = EntityTypeInfo[entityType].icon;
                  const displayTypeName = EntityTypeInfo[entityType].title;

                  return {
                    name: entityType,
                    label: (
                      <Box display="flex" gap={1} alignItems="center">
                        <SvgToIcon IconLogo={Icon} width={"18px"} />
                        {displayTypeName}
                      </Box>
                    ),
                    displayName: displayTypeName,
                  };
                })}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                displayAs="chip"
              />
              {/* ASSIGNEE FILTER */}
              <SearchFilter
                filterName={"Assignee"}
                fieldName={"assignee"}
                options={filterOption.assignee.map((assignee) => {
                  if (assignee === "None") {
                    return {
                      name: assignee,
                      label: (
                        <Box display={"flex"} alignItems={"center"} gap={1}>
                          <SvgToIcon
                            IconLogo={icons.AssigneeIcon}
                            style={{ width: "16px", height: "16px" }}
                          />
                          <Typography variant="caption">No Assignee</Typography>
                        </Box>
                      ),
                      displayName: "No Assignee",
                    };
                  }
                  if (assignee === "All") {
                    return {
                      name: assignee,
                      label: (
                        <Typography variant="caption">All Options</Typography>
                      ),
                      displayName: assignee,
                    };
                  }
                  return {
                    name: assignee?.email,
                    label: <AssigneeLabel assignee={assignee} />,
                    displayName: assignee?.display_name,
                  };
                })}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                fullOptions={filterOption.assignee}
              />
              {/* TICKET FILTER */}
              {(isJiraIntegrated ||
                isAsanaIntegrated ||
                isServiceNowIntegrated) && (
                <FilterMenu
                  filterName={"Tickets"}
                  fieldName={"ticketStatus"}
                  options={Object.values(TICKET_FILTER_OPTIONS).map(
                    (status) => {
                      if (status === "All") {
                        return {
                          name: status,
                          label: (
                            <Typography variant="caption">
                              All Options
                            </Typography>
                          ),
                          displayName: status,
                        };
                      }
                      return {
                        name: status,
                        label: (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="caption">{status}</Typography>
                          </Box>
                        ),
                        displayName: status,
                      };
                    }
                  )}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                  fullOptions={TICKET_FILTER_OPTIONS}
                  displayAs={"chip"}
                />
              )}
              {/* Only New Controls Filter */}
              <FormControlLabel
                sx={{
                  background: colors.tables_button,
                  color: colors.contrast_text_dark,
                  height: "32px",
                  border: "1px solid " + colors.grey_32percent,
                  textTransform: "unset",
                  borderRadius: "8px",
                  margin: "0",
                  gap: 1,
                  padding: "8px",
                }}
                control={
                  <CustomSwitch
                    checked={appliedFilters.showOnlyNewControls}
                    onChange={() => {
                      setAppliedFilters({
                        ...appliedFilters,
                        showOnlyNewControls:
                          !appliedFilters.showOnlyNewControls,
                      });
                    }}
                  />
                }
                label={<Typography variant="caption">New controls</Typography>}
                labelPlacement="start"
              />
              <TimeFilter
                filterName={"Gap Discovered"}
                fieldName={"firstSeen"}
                options={pastTimeOptions}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                filterLogic={filterLogic.firstSeen}
                setFilterLogic={setFilterLogic}
              />
              {detectionsActive && (
                <SwitchFilter
                  filterName={"showActiveDetection"}
                  label={"Active detection"}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                />
              )}
              <SwitchFilter
                filterName={"myFavorites"}
                label={"My favorites"}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
              <ClearFiltersButton
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                onClearClick={() => handleUnselectFilter()}
              />

              <SaveFiltersButton
                appliedFilters={appliedFilters}
                onSaveClick={() => setShowSaveModal(true)}
              />
            </Grid>
          </Box>
          {/* SCAN BUTTON */}
        </Grid>
        <Grid
          container
          display={"flex"}
          justifyContent={"space-between"}
          gap={1}
        >
          {/* SEARCH */}
          <Grid item xs={3}>
            <SearchInput
              fieldName={"searchString"}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Grid>
          <Grid item sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <ScanMenuButton
              isScanLoading={isScanLoading}
              setLoading={setIsScanLoading}
              loadingMessage={"Analyzing systems for security gaps..."}
              gapIdsToScan={selectedRowIDsToScan}
              gapIdsToSnooze={selectedRowIDsToSnooze}
              showLabel={true}
            />
            {/* SNOOZE BUTTON */}
            <SnoozeButton
              key={selectedRowIDsToSnooze.join(",") || "none"}
              controlIds={selectedRowIDsToSnooze}
              snoozeType={SnoozeType.CONTROL}
              initialIsSnooze={false}
              showUnsnoozeButton={false}
            />
            {/* EXPORT BUTTON */}
            <ExportButton handleExport={handleTableExport} />
          </Grid>
        </Grid>
        {/* TABLE */}
        <ControlsTable
          loading={loadingControls}
          controls={currentControls}
          onRowClick={onRowClick}
          onRowSelection={onRowSelection}
          selectedRowIDs={selectedRowIDsToScan}
          refToPass={gridRef}
          viewedControls={viewedControls}
          ticketsByControlId={ticketsByControlId}
          isLoadingTickets={isLoadingTickets}
          sort={location.state?.sort || []}
        />
      </Box>
      {/* DRAWER */}
      <ControlRemediationDrawer
        open={openDrawer}
        onClose={resetSelection}
        selectedTab={DRAWER_TABS.CONTROL_TAB}
        selectedControl={selectedControl}
      />
      {/* POC DIALOG */}
      <LimitsConfirmationDialog
        dialogOpen={pocDialogOpen}
        handleDialogClose={handleDialogClose}
        limitsReached={remainingControlsCount === 0}
        dialogMessageFirstLine={dialogMessageFirstLine}
        dialogMessageSecondLine={dialogMessageSecondLine}
        controlId={controlIdClicked}
        title={dialogTitle}
      />
      {/* LOADING DIALOG */}
      <LoadingDialog message="Loading control..." open={controlLoading} />

      {/* SAVE FILTERS MODAL */}
      <SaveFiltersModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveFilter}
        appliedFilters={appliedFilters}
        isLoading={isSaving}
      />

      {/* EDIT FILTER MODAL */}
      <EditFilterModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
        }}
        onSave={handleUpdateFilter}
        filter={editingFilter}
        isLoading={isUpdating || isSaving}
      />
    </AppPage>
  );
}
